import { IonContent, IonItem, IonLabel, IonModal, IonRadio, IonRadioGroup } from "@ionic/react";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";

// Question interface
interface QuestionData {
    question: string;
    correct: string;
    options: string[];
}


const FillBlanksSenseOrgan: React.FC = () => {
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showImage, setShowImage] = useState(false);
    const [error, setError] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    // Fetch question data from an external source (replace with your actual API or JSON URL)
    const fetchQuestions = async () => {
        try {
            let data: QuestionData[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'FillBlanksSenseOrgan.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/FillBlanksSenseOrgan.json');
                data = module.default;
            }

            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    useEffect(() => {
        // Simulating an API fetch to load questions
        fetchQuestions();
        document.title = "Sense Organs Fill in the Blanks for UKG | General Awareness Worksheet"; // Set page title
    }, []);

    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    const handleOptionClick = (selectedMyOption: string) => {
        console.log(selectedMyOption)
        const currentQuestion = questions[currentIndex];
        setSelectedOption(selectedMyOption);
        if (selectedMyOption === currentQuestion.correct) {
            setError(false);
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                }
                setShowImage(false);
                setError(false);
                setSelectedOption(null)
            }, 5000); // Shortened delay to 3 seconds
        } else {
            setError(true);
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Help UKG kids learn about the five sense organs with easy fill in the blanks questions. Download printable worksheets for classroom or home practice." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/general-awareness/sense-organ/fill-the-blanks" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart seasons additionWord" style={{ marginTop: "0%" }}>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Sense Organs</h1>

                                    <p>
                                        Learning about the five sense organs is essential for UKG students. This <strong>fill in the blanks worksheet</strong> helps young learners identify and understand the function of each sense organ in a fun and interactive way.
                                    </p>

                                    <h2>👁️🧠 What Are the Five Sense Organs?</h2>
                                    <ul>
                                        <li>Eyes – to see</li>
                                        <li>Ears – to hear</li>
                                        <li>Nose – to smell</li>
                                        <li>Tongue – to taste</li>
                                        <li>Skin – to feel</li>
                                    </ul>

                                    <h2>🖊️ Sample Fill in the Blanks Questions</h2>
                                    <ul>
                                        <li>We use our _______ to see. <strong>(Eyes)</strong></li>
                                        <li>The _______ helps us hear sounds. <strong>(Ear)</strong></li>
                                        <li>We smell with our _______. <strong>(Nose)</strong></li>
                                        <li>The _______ helps us taste food. <strong>(Tongue)</strong></li>
                                        <li>We feel things using our _______. <strong>(Skin)</strong></li>
                                    </ul>



                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Sense Organs</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/general-awareness/my-home"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>My Home</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/general-awareness/my-school"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>My School</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Identify the five sense organs</li>
                                        <li>Understand the use of each sense</li>
                                        <li>Improve vocabulary and sentence comprehension</li>
                                    </ul>

                                    <h2>👩‍🏫 Tips for Teachers and Parents</h2>
                                    <ul>
                                        <li>Discuss each sense organ with examples before starting the worksheet</li>
                                        <li>Use real-life objects to explain (e.g., flowers to smell, toys to touch)</li>
                                        <li>Encourage kids to use their own words if they’re unsure</li>
                                    </ul>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Sense Organs</h1>

                                <p>
                                    Learning about the five sense organs is essential for UKG students. This <strong>fill in the blanks worksheet</strong> helps young learners identify and understand the function of each sense organ in a fun and interactive way.
                                </p>

                                <h2>👁️🧠 What Are the Five Sense Organs?</h2>
                                <ul>
                                    <li>Eyes – to see</li>
                                    <li>Ears – to hear</li>
                                    <li>Nose – to smell</li>
                                    <li>Tongue – to taste</li>
                                    <li>Skin – to feel</li>
                                </ul>

                                <h2>🖊️ Sample Fill in the Blanks Questions</h2>
                                <ul>
                                    <li>We use our _______ to see. <strong>(Eyes)</strong></li>
                                    <li>The _______ helps us hear sounds. <strong>(Ear)</strong></li>
                                    <li>We smell with our _______. <strong>(Nose)</strong></li>
                                    <li>The _______ helps us taste food. <strong>(Tongue)</strong></li>
                                    <li>We feel things using our _______. <strong>(Skin)</strong></li>
                                </ul>



                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Sense Organs</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/general-awareness/my-home"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>My Home</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/general-awareness/my-school"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>My School</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Identify the five sense organs</li>
                                    <li>Understand the use of each sense</li>
                                    <li>Improve vocabulary and sentence comprehension</li>
                                </ul>

                                <h2>👩‍🏫 Tips for Teachers and Parents</h2>
                                <ul>
                                    <li>Discuss each sense organ with examples before starting the worksheet</li>
                                    <li>Use real-life objects to explain (e.g., flowers to smell, toys to touch)</li>
                                    <li>Encourage kids to use their own words if they’re unsure</li>
                                </ul>
                            </article>
                            <div className="mt-4 mb-4">
                                <h3 className="mb-5">
                                    {questions[currentIndex]?.question}
                                </h3>
                            </div>
                            <IonRadioGroup onIonChange={(e) => handleOptionClick(e.detail.value)}>
                                {questions[currentIndex]?.options.map((option, index) => (
                                    <IonItem key={index}
                                        className={`grid-item ${selectedOption === option
                                            ? option === questions[currentIndex]?.correct
                                                ? "correct"
                                                : "errorItems"
                                            : ""
                                            }`}>
                                        <IonRadio slot="start" value={option} />
                                        <IonLabel>{option}</IonLabel>
                                    </IonItem>
                                ))}
                            </IonRadioGroup>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default FillBlanksSenseOrgan;
